import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { setMoneyVisible, isMoneyVisible } from '../utils/money';
import { BACKEND_URL } from '../config/runtime';

// Who may see prices, and whether they are currently choosing to.
//
// Two separate ideas, and conflating them is how a screen-share toggle turns into
// a privilege escalation. `canSeeMoney` is the server's answer, sent on /auth/me
// and never derived in the browser. `hidden` is the viewer's own choice, which
// can only ever *remove* figures they are entitled to.
//
// The choice lives in sessionStorage, not localStorage: hiding money is something
// you do for the next ten minutes because somebody is standing behind you, and a
// preference that silently survives until next month is one people forget they set
// and then report as a bug.

const STORAGE_KEY = 'erp.money.hidden';
const MoneyContext = createContext({
  canSeeMoney: false,
  // 'full' | 'document' | 'none'. `canSeeMoney` is the top tier only; the middle
  // one sees the figures printed on the records it keys and nothing the business
  // earned. Kept as a separate value rather than folded into the boolean because
  // the two answer different questions and a screen needs both.
  moneyLevel: 'none',
  moneyHidden: false,
  showMoney: false,
  toggleMoney: () => {},
});

function readStoredHidden() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function MoneyProvider({ children }) {
  const [canSeeMoney, setCanSeeMoney] = useState(false);
  const [moneyLevel, setMoneyLevel] = useState('none');
  const [moneyHidden, setMoneyHidden] = useState(readStoredHidden);

  // The permission arrives with the session. Re-read on every auth change rather
  // than caching it, because impersonation swaps the identity underneath us.
  const loadPermission = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCanSeeMoney(false);
      setMoneyLevel('none');
      return;
    }
    try {
      const base = BACKEND_URL;
      const { data } = await axios.get(`${base}/api/auth/me`);
      setCanSeeMoney(Boolean(data?.can_see_money));
      setMoneyLevel(data?.money_level || (data?.can_see_money ? 'full' : 'none'));
    } catch {
      // Fail closed. A network blip must not turn into "everyone sees costs".
      setCanSeeMoney(false);
      setMoneyLevel('none');
    }
  }, []);

  useEffect(() => {
    loadPermission();
    const onAuth = () => loadPermission();
    window.addEventListener('tokenChanged', onAuth);
    window.addEventListener('storage', onAuth);
    return () => {
      window.removeEventListener('tokenChanged', onAuth);
      window.removeEventListener('storage', onAuth);
    };
  }, [loadPermission]);

  // The document tier sees figures too — the rates and amounts on the records it
  // keys. The server has already removed what that tier may not see (costing,
  // margins, what customers pay), so masking every figure here as well hid the
  // operator's own purchase rates behind locks for no protection at all.
  const mayShowMoney = canSeeMoney || moneyLevel === 'document';
  const showMoney = mayShowMoney && !moneyHidden;

  // Push the answer into the module-level flag so plain formatter functions —
  // the ones inside tables, template literals and PDF-bound strings — can consult
  // it without becoming components.
  useEffect(() => {
    if (isMoneyVisible() !== showMoney) setMoneyVisible(showMoney);
  }, [showMoney]);

  const toggleMoney = useCallback(() => {
    setMoneyHidden((previous) => {
      const next = !previous;
      try { window.sessionStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch { /* private mode */ }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ canSeeMoney, mayShowMoney, moneyLevel, moneyHidden, showMoney, toggleMoney, refreshMoneyPermission: loadPermission }),
    [canSeeMoney, mayShowMoney, moneyLevel, moneyHidden, showMoney, toggleMoney, loadPermission],
  );

  return <MoneyContext.Provider value={value}>{children}</MoneyContext.Provider>;
}

export function useMoney() {
  return useContext(MoneyContext);
}
